import type { IntegrationAuthType, IntegrationDefinition, IntegrationId } from '../types/integration.js';

export const INTEGRATION_REGISTRY: readonly IntegrationDefinition[] = [
  {
    id: 'linear',
    name: 'Linear',
    category: 'project-management',
    description: 'Issue tracking and project management',
    supported_auth_types: ['api_key', 'oauth2'],
    capabilities: [
      { id: 'linear:read-issues', name: 'Read Issues', description: 'View issues, projects, and teams', access: 'read' },
      { id: 'linear:create-issue', name: 'Create Issues', description: 'Create and update issues', access: 'write' },
      { id: 'linear:manage-projects', name: 'Manage Projects', description: 'Create/archive projects and manage team settings', access: 'admin' },
    ],
    cli_tool: {
      package: '@schpet/linear-cli',
      binary: 'linear',
      env_key: 'LINEAR_API_KEY',
      skill_id: 'linear-cli',
      extra_env: { LINEAR_ISSUE_SORT: 'priority' },
      installer: 'npm',
    },
  },
  {
    id: 'github',
    name: 'GitHub',
    category: 'code',
    description: 'Source code hosting, pull requests, and CI/CD',
    supported_auth_types: ['api_key', 'oauth2'],
    // ENG-7015: customer-installable native — OAuth-first in the connect UI.
    installable: { category: 'Code', authTypes: ['oauth2', 'api_key'] },
    capabilities: [
      { id: 'github:read-repos', name: 'Read Repositories', description: 'View repos, issues, and PRs', access: 'read' },
      { id: 'github:write-code', name: 'Write Code', description: 'Push commits and create PRs', access: 'write' },
      { id: 'github:manage-repos', name: 'Manage Repositories', description: 'Create/delete repos and manage settings', access: 'admin' },
    ],
    cli_tool: {
      package: 'gh',
      binary: 'gh',
      env_key: 'GITHUB_TOKEN',
      skill_id: 'gh-cli',
      // ENG-6206: `brew` never installs on the Linux fleet (root-on-AL2023,
      // no Homebrew) — gh was permanently missing. Use an OS-detecting script
      // that installs from GitHub's official repos: dnf (AL2023 / RHEL),
      // apt (Debian / Ubuntu), and brew (macOS hosts). The catalog is the
      // trust boundary — this string is source-controlled, never runtime data.
      installer: 'script',
      script:
        'if command -v dnf >/dev/null 2>&1; then curl -fsSL https://cli.github.com/packages/rpm/gh-cli.repo -o /etc/yum.repos.d/gh-cli.repo && dnf install -y gh; elif command -v apt-get >/dev/null 2>&1; then curl -fsSL https://cli.github.com/packages/githubcli-archive-keyring.gpg -o /usr/share/keyrings/githubcli-archive-keyring.gpg && chmod go+r /usr/share/keyrings/githubcli-archive-keyring.gpg && echo "deb [arch=$(dpkg --print-architecture) signed-by=/usr/share/keyrings/githubcli-archive-keyring.gpg] https://cli.github.com/packages stable main" > /etc/apt/sources.list.d/github-cli.list && apt-get update && apt-get install -y gh; elif command -v brew >/dev/null 2>&1; then brew install gh; else echo "gh: no supported installer (need dnf, apt-get, or brew)" >&2; exit 1; fi',
    },
  },
  {
    id: 'google-workspace',
    name: 'Google Workspace',
    category: 'workspace-productivity',
    description: 'Gmail, Calendar, Drive, Sheets, Docs, and Chat',
    supported_auth_types: ['oauth2'],
    capabilities: [
      { id: 'gws:read-email', name: 'Read Email', description: 'Read Gmail messages, threads, and labels', access: 'read' },
      { id: 'gws:send-email', name: 'Send Email', description: 'Send, reply, and forward emails', access: 'write' },
      { id: 'gws:read-calendar', name: 'Read Calendar', description: 'View events and agendas', access: 'read' },
      { id: 'gws:manage-calendar', name: 'Manage Calendar', description: 'Create, update, and delete events', access: 'write' },
      { id: 'gws:read-drive', name: 'Read Drive', description: 'List and download files', access: 'read' },
      { id: 'gws:write-drive', name: 'Write Drive', description: 'Upload, create, and share files', access: 'write' },
      { id: 'gws:read-sheets', name: 'Read Sheets', description: 'Read spreadsheet values', access: 'read' },
      { id: 'gws:write-sheets', name: 'Write Sheets', description: 'Append and update spreadsheet data', access: 'write' },
      { id: 'gws:read-docs', name: 'Read Docs', description: 'Read document content', access: 'read' },
      { id: 'gws:write-docs', name: 'Write Docs', description: 'Create and append to documents', access: 'write' },
      { id: 'gws:chat', name: 'Chat', description: 'Send messages to Google Chat spaces', access: 'write' },
    ],
    cli_tool: {
      package: '@googleworkspace/cli',
      binary: 'gws',
      env_key: 'GOOGLE_WORKSPACE_CLI_TOKEN',
      skill_id: 'gws-cli',
      installer: 'npm',
    },
  },
  {
    id: 'gcloud',
    name: 'Google Cloud SDK',
    category: 'infrastructure',
    description: 'Google Cloud Platform CLI — manage Compute Engine, Cloud Storage, IAM, Cloud Run, Cloud SQL, BigQuery, and Pub/Sub from a single binary',
    supported_auth_types: ['oauth2', 'managed'],
    capabilities: [
      { id: 'gcloud:read', name: 'Read GCP Resources', description: 'List and describe projects, instances, buckets, IAM, and service configs', access: 'read' },
      { id: 'gcloud:write', name: 'Write GCP Resources', description: 'Create and update GCP resources (compute, storage, IAM, run, etc.)', access: 'write' },
      { id: 'gcloud:admin', name: 'Admin GCP Resources', description: 'Destructive operations: delete projects, IAM bindings, instances. Restrict with a guardrail that blocks destructive gcloud/gsutil/bq verbs.', access: 'admin' },
    ],
    cli_tool: {
      package: 'google-cloud-sdk',
      binary: 'gcloud',
      env_key: 'GOOGLE_APPLICATION_CREDENTIALS',
      // gcloud ships as a homebrew cask on macOS (`brew install --cask google-cloud-sdk`)
      // and via curl-installed tarball elsewhere. Neither matches the simple `brew install
      // <package>` or `npm install -g <package>` shape, so leave install to the operator.
      installer: 'manual',
    },
    docs_url: 'https://cloud.google.com/sdk',
  },
  {
    id: 'xero',
    name: 'Xero',
    category: 'accounting',
    description: 'Cloud accounting — financial reports, transactions, and account balances',
    supported_auth_types: ['oauth2'],
    // ENG-7015: customer-installable native.
    installable: { category: 'Accounting', authTypes: ['oauth2'] },
    capabilities: [
      { id: 'xero:read-reports', name: 'Read Reports', description: 'Pull P&L, balance sheet, and trial balance reports', access: 'read' },
      { id: 'xero:read-accounts', name: 'Read Accounts', description: 'View chart of accounts and account balances', access: 'read' },
      { id: 'xero:read-transactions', name: 'Read Transactions', description: 'View bank transactions, invoices, and journal entries', access: 'read' },
      { id: 'xero:read-contacts', name: 'Read Contacts', description: 'View customers, suppliers, and contact groups', access: 'read' },
      { id: 'xero:manage-settings', name: 'Manage Settings', description: 'Manage org settings and chart of accounts', access: 'admin' },
    ],
  },
  {
    id: 'granola',
    name: 'Granola',
    category: 'knowledge',
    description: 'Meeting notes search — query transcripts, summaries, and folders from Granola',
    // Granola uses a remote streamable-HTTP MCP with PKCE + Dynamic Client
    // Registration. End-user OAuth is brokered by the webapp (ENG-4693)
    // through the shared /integrations/oauth/authorize → /callback path
    // (ENG-4694), and the access_token is injected into .mcp.json via the
    // generic bearer-header path. No host-side action required from the
    // operator beyond running the one-time DCR registration script at
    // deploy time.
    supported_auth_types: ['oauth2'],
    capabilities: [
      { id: 'granola:search-meetings', name: 'Search Meetings', description: 'Browse meetings, search content, and chat with notes (query_granola_meetings, list_meetings, get_meetings)', access: 'read' },
      { id: 'granola:read-transcripts', name: 'Read Transcripts', description: 'Access raw meeting transcripts (paid plans only — get_meeting_transcript)', access: 'read' },
      { id: 'granola:list-folders', name: 'List Folders', description: 'View accessible meeting folders (paid plans only — list_meeting_folders)', access: 'read' },
    ],
    docs_url: 'https://docs.granola.ai/docs/api/mcp',
    beta: true,
  },
  {
    id: 'brand-ninja',
    name: 'Brand Ninja',
    category: 'social',
    description: 'Brand-aligned content generation: submit async content requests, track them, and discover publishing channels. Wired as the hosted Brand Ninja External-Content MCP at https://ext-api.app.brandninja.ai/v1/mcp.',
    // ENG-6820: same remote streamable-HTTP MCP + OAuth pattern as Granola.
    // Brand Ninja's server implements the full MCP discovery chain (RFC
    // 9728/8414/7591); auth is OAuth 2.0 authorization-code with PKCE (S256)
    // and a public client registered via one-time Dynamic Client Registration
    // (scripts/dcr-register.ts against https://ext-api.app.brandninja.ai/v1/oauth/register
    // → OAUTH_BRAND_NINJA_CLIENT_ID). End-user consent is brokered by the
    // webapp through the shared /integrations/oauth/authorize → /callback path,
    // and the access_token is injected into .mcp.json via the generic
    // bearer-header path (OAUTH_PROVIDERS.brand-ninja.mcpUrl). No host-side
    // action beyond the deploy-time DCR registration.
    supported_auth_types: ['oauth2'],
    capabilities: [
      { id: 'brand-ninja:generate-content', name: 'Generate Content', description: 'Submit async brand-aligned content-generation requests and poll their status (submit_content_request, get_content_status, list_content_requests)', access: 'write' },
      { id: 'brand-ninja:list-channels', name: 'List Channels', description: 'Discover the publishing channels available to the account, metadata only (list_channels)', access: 'read' },
      { id: 'brand-ninja:read-credentials', name: 'Read Credentials', description: 'Read-only External-API credential metadata, secrets stripped. Requires the elevated external-api/admin scope (list_credentials)', access: 'admin' },
    ],
    docs_url: 'https://ext-api.app.brandninja.ai/v1/mcp',
    beta: true,
  },
  {
    id: 'kajabi',
    name: 'Kajabi',
    category: 'crm',
    description: 'Run a Kajabi creator business from chat: read contacts, products, offers, and analytics, manage contact tags & segments, and draft email broadcasts & sequences.',
    // Same remote streamable-HTTP MCP + OAuth pattern as Granola/Brand Ninja.
    // Kajabi's Doorkeeper AS implements the MCP discovery chain (RFC
    // 9728/8414/7591); auth is OAuth 2.0 authorization-code with PKCE (S256)
    // and a public client registered via one-time Dynamic Client Registration
    // (scripts/dcr-register.ts against https://mcp.kajabi.com/mcp/oauth/register
    // → OAUTH_KAJABI_CLIENT_ID; register with --scope 'read write:contacts
    // write:emails' since Doorkeeper caps a dynamic client to its registered
    // scopes). End-user consent is brokered by the webapp through the shared
    // /integrations/oauth/authorize → /callback path, and the access_token is
    // injected into .mcp.json via the generic bearer-header path
    // (OAUTH_PROVIDERS.kajabi.mcpUrl). Every Kajabi tool is site-scoped — agents
    // call list_sites/select_site first. No host-side action beyond the
    // deploy-time DCR registration.
    supported_auth_types: ['oauth2'],
    capabilities: [
      { id: 'kajabi:read', name: 'Read & Discover', description: 'List sites and read contacts, products, offers, purchases, and revenue/contacts analytics (list_sites, select_site, search_contacts, get_contact, list_offers, get_offer, search_products, get_revenue_analytics, …)', access: 'read' },
      { id: 'kajabi:contacts', name: 'Manage Contacts', description: 'Create and apply contact tags, and create/update saved contact segments (create_tag, tag_contact, untag_contact, create_segment, update_segment)', access: 'write' },
      { id: 'kajabi:emails', name: 'Manage Emails', description: 'Read and draft email broadcasts and sequences — drafts only, sending stays a human action in Kajabi (create_broadcast, create_sequence, list_broadcasts, get_sequence)', access: 'write' },
    ],
    docs_url: 'https://help.kajabi.com/articles/api-integrations/connect-kajabi-to-claude-or-chatgpt',
    beta: true,
  },
  {
    id: 'anchor-browser',
    name: 'Anchor Browser',
    category: 'workspace-productivity',
    description: 'Cloud browser for agents — drive any website that lacks an API (LinkedIn, Sales Navigator, supplier portals) via a hosted, stealth Chromium with persistent-login profiles. Wired as Anchor\'s HOSTED streamable-HTTP MCP at https://api.anchorbrowser.io/mcp.',
    // ENG-5855: api-key header auth (NOT OAuth, NOT a local stdio package).
    // The manager writes ANCHOR_BROWSER_API_KEY to .env.integrations from the
    // stored api_key credential; the hosted MCP authenticates on the
    // `anchor-api-key` header. The `anchor-session-id` header binds an
    // authenticated profile session — its value is minted per-session by the
    // manager (ENG-5857); until then `envDefaults` seeds it empty so
    // stateless browsing works and no literal `${...}` placeholder ships.
    // Tool surface (25 `anchor_*` tools) is the hosted MCP's, validated in
    // the ENG-5854 spike (docs/spikes/eng-5854-anchor-browser-persistent-login.md).
    supported_auth_types: ['api_key'],
    capabilities: [
      { id: 'anchor-browser:browse', name: 'Browse & Read', description: 'Navigate and read pages — snapshot, screenshot, page HTML, tabs, console, network requests, wait (anchor_navigate, anchor_snapshot, anchor_take_screenshot, anchor_get_body_html, anchor_tab_list, anchor_console_messages, anchor_network_requests, anchor_wait_for, anchor_navigate_back/forward)', access: 'read' },
      { id: 'anchor-browser:interact', name: 'Interact', description: 'Act on pages — click, type, hover, drag, select options, press keys, handle dialogs, upload files, resize, manage tabs (anchor_click, anchor_type, anchor_hover, anchor_drag, anchor_select_option, anchor_press_key, anchor_handle_dialog, anchor_file_upload, anchor_resize, anchor_tab_new/select/close, anchor_close)', access: 'write' },
      { id: 'anchor-browser:export', name: 'Export & Codegen', description: 'Save the current page as PDF and generate Playwright code for a scenario (anchor_pdf_save, anchor_generate_playwright_code)', access: 'write' },
    ],
    docs_url: 'https://docs.anchorbrowser.io/introduction',
    beta: true,
    remoteMcp: {
      type: 'http',
      url: 'https://api.anchorbrowser.io/mcp',
      // ENG-6993 / ADR-0033: the api-key credential header now goes through the
      // structured `auth` field — the env var (ANCHOR_BROWSER_API_KEY) is
      // DERIVED from this integration's definition_id + credential_ref, so it
      // is scoped to Anchor and can't reference another integration's secret
      // (C1). Renders byte-identically to the previous verbatim header.
      auth: { scheme: 'header', header_name: 'anchor-api-key', credential_ref: 'api_key' },
      // The dynamic session header stays here (not a credential — minted per
      // session by ENG-5857; empty default below until then).
      headers: {
        'anchor-session-id': '${ANCHOR_BROWSER_SESSION_ID}',
      },
      // ENG-5857 mints the real session id; default empty so the header
      // resolves cleanly (no profile bound → ephemeral session) until then.
      envDefaults: { ANCHOR_BROWSER_SESSION_ID: '' },
    },
  },
  {
    id: 'deck',
    name: 'Deck',
    category: 'workspace-productivity',
    description:
      'Computer-use agents that operate any software through its real interface (no API required) and return schema-validated results. A higher-level alternative to Anchor Browser: Deck owns the auth lifecycle (encrypted credential vault, login, MFA, CAPTCHA) and provisions isolated desktop sessions on demand. Augmented Team manages Deck access for you and gives each agent its own isolated Deck workspace, so there is no credential to enter.',
    // Deck is REST-only (base https://api.deck.co/v2, Bearer `sk_live_` account
    // key) — it ships NO MCP server, so unlike anchor-browser there is no
    // `remoteMcp`/`nativeMcp` drop-in; the agent-facing tools are brokered
    // server-side (deck-broker.ts). Deck is the first PREMIUM integration:
    // Augmented owns ONE Deck account that every customer agent's runs bill back
    // to (per-org charging is tracked in ENG-6920, not yet live), so the account
    // key is a single platform-held secret (`DECK_ACCOUNT_KEY`), NOT a per-agent
    // credential. Auth type is therefore `none` — customers never enter a key.
    // Per-agent isolation is modelled on Deck's first-class resources: that one
    // key provisions one Deck agent (`agt_`) + vault credential (`cred_`) per
    // Augmented agent via POST /:id/provision-deck; the ids land in
    // `agent_integrations.config` (deck_agent_id / deck_credential_id), so
    // revocation + audit happen at the per-agent Deck-resource level without a
    // distinct API key per agent (Deck exposes no key-minting admin API).
    supported_auth_types: ['none'],
    capabilities: [
      { id: 'deck:provision', name: 'Provision Agent Access', description: 'Provision a per-agent Deck agent and vault credential under the account key (create_agent, create_credential)', access: 'admin' },
      { id: 'deck:run', name: 'Run Tasks', description: 'Submit tasks to the agent and read schema-validated structured results (run_task, get_task_run)', access: 'write' },
      { id: 'deck:observe', name: 'Observe Sessions', description: 'Read isolated session state, screenshots, and agent-reasoning artifacts (get_session)', access: 'read' },
    ],
    docs_url: 'https://docs.deck.co/',
    beta: true,
    // ENG-6920: Deck is the first PREMIUM integration. Unlike the customer-auth
    // integrations, every Deck run bills back to Augmented's single account
    // key, and Deck is usage-priced — so it is gated on a per-org opt-in and
    // metered. ENG-7032: `meters` declares the billable operation (run_task,
    // billed per run); the priced rate card (integration_rate_cards) holds the
    // amount ($1.00 USD / A$1.50 per run, the per-run v1 decision). The
    // event_type must match what deck-broker writes to integration_usage_events.
    premium: {
      pricing: 'usage',
      note: 'Billed per Deck task run.',
      meters: [{ event_type: 'run_task', unit: 'run' }],
    },
  },
  {
    id: 'elevenlabs',
    name: 'ElevenLabs',
    category: 'media',
    description:
      'Speech-to-text for inbound voice notes. When a teammate sends an agent a voice message (Slack, Telegram, etc.), the agent uploads the audio and gets back an accurate transcript via ElevenLabs Scribe, so a voice note is no longer a black box. Augmented Team manages ElevenLabs access for you - there is no key to enter.',
    // ElevenLabs is REST-only for our use (POST /v1/speech-to-text, `xi-api-key`
    // header, NOT Bearer) — it ships no MCP server, so the agent-facing tools are
    // brokered server-side (scribe-broker.ts). It is a PREMIUM integration on the
    // Deck model (ADR-0031, epic ENG-6920): Augmented owns ONE ElevenLabs account
    // that every customer agent's transcriptions bill back to, so the account key
    // is a single platform-held secret (`ELEVENLABS_ACCOUNT_KEY`), NOT a per-agent
    // credential. Auth type is therefore `none` — customers never enter a key.
    // Usage is metered in audio-seconds at the broker chokepoint and gated on a
    // per-org opt-in + monthly cap. (TTS for Augmented Live voiceover is a
    // separate surface — ENG-7048 — that reuses the same account key.)
    supported_auth_types: ['none'],
    capabilities: [
      { id: 'elevenlabs:transcribe', name: 'Transcribe Voice Notes', description: 'Upload an inbound audio file and transcribe it to text via ElevenLabs Scribe (scribe_create_upload, scribe_transcribe)', access: 'write' },
      // ENG-7048: text-to-speech voiceover for Augmented Live. Surfaced as the
      // agt_live.generate_voiceover tool (not a standalone scribe_* tool); shares
      // this one platform account key + the same per-org budget.
      { id: 'elevenlabs:tts', name: 'Generate Voiceover', description: 'Synthesize a spoken-voice MP3 voiceover from text for an Augmented Live page via ElevenLabs text-to-speech (agt_live.generate_voiceover)', access: 'write' },
      // ENG-7089: instrumental music generation for Augmented Live. Surfaced as the
      // agt_live.generate_music tool; shares this one platform account key + the
      // same per-org budget.
      { id: 'elevenlabs:music', name: 'Generate Music', description: 'Compose an instrumental MP3 music track from a text prompt for an Augmented Live page via ElevenLabs Music (agt_live.generate_music)', access: 'write' },
    ],
    // Capabilities index — covers speech-to-text, text-to-speech, and music, since
    // the integration now advertises elevenlabs:transcribe, elevenlabs:tts, and
    // elevenlabs:music.
    docs_url: 'https://elevenlabs.io/docs/capabilities',
    beta: true,
    // ENG-7005 / ENG-7048: premium (billable). Both surfaces bill back to
    // Augmented's single account key and are usage-priced, so the integration is
    // gated on a per-org opt-in and metered. The two surfaces share the one
    // `elevenlabs` definition (and so the one per-org monthly budget). Pricing
    // amounts live in integration_rate_cards; this only declares the model.
    premium: {
      pricing: 'usage',
      note: 'Billed on audio transcribed (per second), voiceover synthesized (per character), and music generated (per second).',
      // ENG-7032: each surface meters its own event in its own physical unit; the
      // matching integration_rate_cards rows price them. Until a rate is seeded,
      // that event prices at 0.
      meters: [
        { event_type: 'transcribe', unit: 'audio_second' },
        { event_type: 'tts', unit: 'character' },
        { event_type: 'music', unit: 'second' },
      ],
    },
  },
  {
    id: 'postiz',
    name: 'Postiz',
    category: 'social',
    description: 'Open-source social-media scheduling and publishing — schedule posts, list connected platforms, and upload media. Self-hosted-aware (defaults to Postiz Cloud at https://api.postiz.com).',
    // Postiz also supports OAuth2 ('pos_'-prefixed tokens) but the public docs
    // for the authorize/token URL shape are sparse — wired API-key-first; the
    // OAuth path lands as a follow-up once we've confirmed the flow against
    // a live instance.
    supported_auth_types: ['api_key'],
    capabilities: [
      { id: 'postiz:list', name: 'List Posts & Platforms', description: 'List connected social platforms (GET /integrations) and previously scheduled posts', access: 'read' },
      { id: 'postiz:publish', name: 'Publish Posts', description: 'Create and schedule posts across the connected platforms (POST /posts)', access: 'write' },
      { id: 'postiz:upload', name: 'Upload Media', description: 'Upload images and video for use in posts (POST /upload)', access: 'write' },
    ],
    docs_url: 'https://docs.postiz.com/public-api/introduction',
    // Beta until we've verified the npx-based community MCP server
    // (antoniolg/postiz-mcp) end-to-end against a real Postiz instance.
    // The 30-req/hr public API rate limit also wants real-world
    // validation before we drop the beta flag.
    beta: true,
  },
  {
    id: 'higgsfield',
    name: 'Higgsfield',
    category: 'media',
    description: 'Generative media — image (Soul, Nano Banana) and video (Kling, Veo, Seedance) generation, character training (Soul ID), and generation history. Remote streamable-HTTP MCP at https://mcp.higgsfield.ai/mcp.',
    // Same OAuth pattern as Granola: Claude Code brokers the browser
    // sign-in at runtime; nothing for the manager API to provision.
    supported_auth_types: ['none'],
    capabilities: [
      { id: 'higgsfield:generate-image', name: 'Generate Image', description: 'Create images via Soul, Nano Banana, and other image models — up to 4K. Includes Soul ID character consistency.', access: 'write' },
      { id: 'higgsfield:generate-video', name: 'Generate Video', description: 'Create videos via Kling, Veo, Seedance — up to 15s. Includes UGC, product review, TV spot presets.', access: 'write' },
      { id: 'higgsfield:read-history', name: 'Read History', description: 'Browse generation history for iterative workflows; reuse prior outputs as references.', access: 'read' },
    ],
    docs_url: 'https://higgsfield.ai/mcp',
    beta: true,
  },
  {
    id: 'qmd',
    name: 'QMD Memory Search',
    category: 'knowledge',
    description: 'Local-first memory search sidecar — BM25 + vector search + reranking over agent memory files',
    supported_auth_types: ['none'],
    // ENG-7264: QMD is DEPRECATED - no longer offered in the Add Integration
    // picker (the dialog also drops it via DEPRECATED_PICKER_IDS as a belt-and-
    // suspenders for its DB toolkit row). The entry is retained, sans
    // `installable`, so existing agent installs keep resolving their nativeMcp.
    // Removing `installable` also drops it from the org native allowlist, so new
    // installs are refused at the API too.
    cli_tool: {
      package: '@tobilu/qmd',
      binary: 'qmd',
      env_key: '',
      installer: 'npm',
    },
    capabilities: [
      { id: 'qmd:search', name: 'Search Memory', description: 'Semantic + keyword search over indexed memory files', access: 'read' },
      { id: 'qmd:get', name: 'Get Memory', description: 'Read memory files by path and line range', access: 'read' },
    ],
    beta: true,
    // ENG-5815: migrated from buildMcpJson's hardcoded if-block. qmd is
    // the simplest of the four pre-data-driven entries — no env, no
    // conditional logic, just `qmd mcp`. The byte-identical render is
    // pinned by claudecode-qmd-data-driven.test.ts.
    nativeMcp: {
      command: 'qmd',
      args: ['mcp'],
    },
  },
  {
    id: 'v0',
    name: 'v0 by Vercel',
    category: 'ui-generation',
    description: 'Programmatic UI generation — generate React + Tailwind + shadcn/ui components and full apps from natural language prompts',
    supported_auth_types: ['api_key'],
    beta: true,
    capabilities: [
      {
        id: 'v0:generate-ui',
        name: 'Generate UI',
        description: 'Create React components and full apps from a natural language prompt',
        access: 'write',
        required_scopes: ['chats:create'],
      },
      {
        id: 'v0:iterate-ui',
        name: 'Iterate UI',
        description: 'Send follow-up prompts to refine a previously generated component',
        access: 'write',
        required_scopes: ['chats:send'],
      },
      {
        id: 'v0:read-chats',
        name: 'Read Chats',
        description: 'Retrieve chat history, generated files, and demo URLs',
        access: 'read',
        required_scopes: ['chats:read'],
      },
      {
        id: 'v0:manage-projects',
        name: 'Manage Projects',
        description: 'Create and manage v0 project containers for versioned generation history',
        access: 'write',
        required_scopes: ['projects:write'],
      },
      {
        id: 'v0:deploy',
        name: 'Deploy to Vercel',
        description: 'Deploy a generated version to Vercel and receive a live URL',
        access: 'write',
        required_scopes: ['deployments:create'],
      },
    ],
    docs_url: 'https://v0.dev/docs/api/platform/overview',
  },
  {
    id: 'pika',
    name: 'Pika',
    category: 'media',
    description: 'AI video meeting agent — join Google Meet and Zoom calls with a custom avatar and cloned voice via PikaStreaming',
    supported_auth_types: ['api_key'],
    // ENG-7015: customer-installable native — the one straggler not in the DB
    // catalog, now carried by this single shared source.
    installable: { category: 'Media', authTypes: ['api_key'] },
    capabilities: [
      { id: 'pika:join-meeting', name: 'Join Meeting', description: 'Join a video meeting as an AI participant with avatar and voice', access: 'write' },
      { id: 'pika:leave-meeting', name: 'Leave Meeting', description: 'Leave an active video meeting session', access: 'write' },
      { id: 'pika:generate-avatar', name: 'Generate Avatar', description: 'Generate an AI avatar image for video calls', access: 'write' },
      { id: 'pika:clone-voice', name: 'Clone Voice', description: 'Clone a voice from an audio recording', access: 'write' },
    ],
    cli_tool: {
      package: 'pika-skills',
      binary: 'python3',
      env_key: 'PIKA_DEV_KEY',
      skill_id: 'pikastream-video-meeting',
      // python3 is part of the host bootstrap baseline — skills are fetched
      // separately. Don't try to auto-install python via npm/brew.
      installer: 'manual',
    },
    docs_url: 'https://github.com/Pika-Labs/Pika-Skills',
  },
  {
    id: 'claude-code',
    name: 'Claude Code',
    category: 'code',
    description: 'Claude Code AI agent runtime — code editing, task execution, file management, and development workflows',
    supported_auth_types: ['api_key', 'none'],
    capabilities: [
      { id: 'claude-code:edit-code', name: 'Edit Code', description: 'Read, write, and edit source files', access: 'write' },
      { id: 'claude-code:run-tasks', name: 'Run Tasks', description: 'Execute bash commands and development tasks', access: 'write' },
      { id: 'claude-code:search', name: 'Search Code', description: 'Search files and grep codebase', access: 'read' },
      { id: 'claude-code:git', name: 'Git Operations', description: 'Commit, branch, push, and manage version control', access: 'write' },
    ],
    cli_tool: {
      package: '@anthropic-ai/claude-code',
      binary: 'claude',
      env_key: 'ANTHROPIC_API_KEY',
      // Claude Code is installed by the host bootstrap / operator setup —
      // don't attempt a second install from the manager poll.
      installer: 'manual',
    },
    docs_url: 'https://docs.anthropic.com/en/docs/claude-code',
  },
  {
    id: 'xurl',
    name: 'xurl (X API)',
    category: 'social',
    description: "Official X (Twitter) API CLI — a curl-like tool for X's REST and streaming endpoints with OAuth 2.0 PKCE, OAuth 1.0a, and bearer-token auth",
    supported_auth_types: ['api_key'],
    // ENG-7015: customer-installable native. The connect UI also offers a
    // keyless "none" option (run against the app's bearer token) that the
    // runtime capability set above does not enumerate.
    installable: { category: 'Social', authTypes: ['none', 'api_key'] },
    capabilities: [
      { id: 'xurl:read', name: 'Read X API', description: 'Call GET endpoints (users, tweets, timelines, search)', access: 'read' },
      { id: 'xurl:write', name: 'Write X API', description: 'Post tweets, reply, like, and retweet', access: 'write' },
      { id: 'xurl:stream', name: 'Stream X API', description: 'Consume filtered and sampled stream endpoints', access: 'read' },
      { id: 'xurl:media', name: 'Upload Media', description: 'Chunked upload of images and video to the X media endpoints', access: 'write' },
    ],
    cli_tool: {
      package: '@xdevplatform/xurl',
      binary: 'xurl',
      env_key: 'X_BEARER_TOKEN',
      skill_id: 'xurl-cli',
      // xurl is a Go binary distributed through homebrew tap; operator
      // installs via `brew install xdevplatform/tap/xurl`. Mark manual
      // for now — add a dedicated `tap` installer in a follow-up if more
      // brew-tap tools land.
      installer: 'manual',
    },
    docs_url: 'https://github.com/xdevplatform/xurl',
  },
  {
    id: 'coderabbit',
    name: 'CodeRabbit',
    category: 'code',
    description: 'AI-powered code review CLI for local and pre-push review runs',
    supported_auth_types: ['none'],
    capabilities: [
      { id: 'coderabbit:review', name: 'Review Changes', description: 'Run a local CodeRabbit review over staged or branch changes', access: 'read' },
    ],
    cli_tool: {
      package: '',
      binary: 'coderabbit',
      env_key: '',
      installer: 'script',
      script: 'curl -fsSL https://cli.coderabbit.ai/install.sh | sh',
    },
    docs_url: 'https://www.coderabbit.ai/cli',
  },
  {
    id: 'aws',
    name: 'AWS',
    category: 'infrastructure',
    description: "Amazon Web Services — query AWS APIs (EC2, S3, IAM, Lambda, etc.) via AWS Labs' official AWS API MCP server",
    supported_auth_types: ['api_key', 'managed', 'none'],
    capabilities: [
      { id: 'aws:read', name: 'Read AWS Resources', description: 'List and describe AWS resources across services (EC2, S3, IAM, Lambda, …)', access: 'read' },
      { id: 'aws:write', name: 'Write AWS Resources', description: 'Create and update AWS resources. Pair with an aws-no-destructive-ops guardrail.', access: 'write' },
    ],
    docs_url: 'https://github.com/awslabs/mcp/tree/main/src/aws-api-mcp-server',
    beta: true,
    // ENG-5815: first integration shipped purely via the data-driven
    // path — buildMcpJson never grew an `aws` if-block. The AWS Labs
    // AWS API MCP server runs through uvx (Python tooling), which the
    // host bootstrap installs alongside python3. Credentials are
    // resolved via the standard AWS_* env / shared credentials file
    // chain on the host; the spec doesn't override them.
    nativeMcp: {
      command: 'uvx',
      args: ['awslabs.aws-api-mcp-server@latest'],
      env: {
        AWS_REGION: '{{empty_if_no_env.AWS_REGION}}',
        AWS_PROFILE: '{{empty_if_no_env.AWS_PROFILE}}',
        PATH: '{{process_env.PATH}}',
        HOME: '{{process_env.HOME}}',
      },
    },
  },
  {
    // ENG-6195: admin-only debugging surface for Integrity Labs STAFF agents.
    // Provisions the @integrity-labs/augmented-admin-mcp stdio broker, which
    // reads end-user agent diagnostics cross-org via /admin/debug/*. `beta` so
    // it is visible/enable-able only by admin-email-domain users; the API
    // double-gates every call on the caller's owning org `is_internal = true`.
    // auth `none` — no end-user OAuth; the host JWT (org_id claim) is the
    // credential. NOT a customer integration; do not promote to `published`.
    id: 'augmented-admin',
    name: 'Augmented Admin Debug',
    category: 'infrastructure',
    description: 'Integrity Labs staff-only: cross-org agent/host/integration/alert diagnostics for troubleshooting managed agents.',
    supported_auth_types: ['none'],
    beta: true,
    capabilities: [
      { id: 'augmented-admin:read-diagnostics', name: 'Read Diagnostics', description: 'Cross-org read of agent, host, integration, and alert diagnostics (projection only — never credentials or transcripts).', access: 'read' },
    ],
  },
  {
    // ENG-7023 (ADR-0031/0032): the per-org self-troubleshoot surface for the
    // `system_support` concierge agent. Provisions the
    // @integrity-labs/augmented-support-mcp stdio broker, which reads the
    // agent's OWN org diagnostics and proposes self-remediation writes
    // (create_agent) through the server-rendered HITL approval gate, all via
    // /host/support/*. `beta` while the concierge rolls out gradually (ENG-6975);
    // auth `none` - no end-user OAuth, the host JWT (org_id claim) is the
    // credential and the org-lock. NOT a customer-selectable integration: it is
    // attached automatically to system_support agents at provisioning.
    id: 'augmented-support',
    name: 'Augmented Support',
    category: 'infrastructure',
    description: "Per-org self-troubleshoot concierge: reads your org's agents, hosts, integrations, alerts, flags, and audit log, files support/feature requests, and proposes new agents for human approval - all scoped to your own organization.",
    supported_auth_types: ['none'],
    beta: true,
    capabilities: [
      { id: 'augmented-support:read-diagnostics', name: 'Read Diagnostics', description: "Read your own org's agents, hosts, integrations, alerts, flags, and audit log (projection only - never credentials or transcripts).", access: 'read' },
      { id: 'augmented-support:file-requests', name: 'File Requests', description: 'File bug / feature / integration requests to Augmented Team support.', access: 'write' },
      { id: 'augmented-support:propose-writes', name: 'Propose Self-Remediation', description: 'Propose creating an agent in your own org; executed only after a human approves a server-rendered diff.', access: 'write' },
    ],
  },
  {
    id: 'firecrawl',
    name: 'Firecrawl',
    category: 'knowledge',
    description:
      'Web data for agents: scrape, map, crawl and search any website for clean structured data, plus scheduled change-detection monitors whose updates arrive in the agent\'s direct-chat. Augmented Team manages Firecrawl access for you - there is no key to enter.',
    // ENG-7217: Firecrawl migrates from a customer-API-key stdio MCP
    // (`npx firecrawl-mcp`, FIRECRAWL_API_KEY) to a PREMIUM, platform-managed
    // integration on the Deck / ElevenLabs model (ADR-0031, epic ENG-6920):
    // Augmented holds ONE Firecrawl account key (`FIRECRAWL_ACCOUNT_KEY`) that
    // every customer agent's web-data calls bill back to, so the key is a single
    // platform-held secret, NOT a per-agent credential. Auth type is therefore
    // `none` - customers never enter a key. The agent-facing tools are brokered
    // server-side (firecrawl-broker.ts, reusing the official @mendable/firecrawl-js
    // SDK) rather than via the stdio MCP, so usage can be metered at the one
    // control-plane chokepoint and gated on a per-org opt-in + monthly USD cap.
    supported_auth_types: ['none'],
    capabilities: [
      { id: 'firecrawl:scrape', name: 'Scrape Pages', description: 'Fetch a single URL as clean markdown / structured data (firecrawl_scrape).', access: 'write' },
      { id: 'firecrawl:search', name: 'Web Search', description: 'Search the web and return ranked results, optionally scraped (firecrawl_search).', access: 'write' },
      { id: 'firecrawl:map', name: 'Map Site', description: 'Discover the URLs on a site without scraping them (firecrawl_map).', access: 'read' },
      { id: 'firecrawl:crawl', name: 'Crawl Site', description: 'Crawl a site to many pages; start a job and poll it (firecrawl_crawl, firecrawl_crawl_status).', access: 'write' },
      { id: 'firecrawl:monitor', name: 'Monitor Changes', description: 'Create / list / delete scheduled change-detection monitors delivered into direct-chat (firecrawl_monitor_*).', access: 'write' },
    ],
    docs_url: 'https://docs.firecrawl.dev/',
    beta: true,
    // ENG-7217: premium (billable). Firecrawl bills internally in credits and we
    // pay per credit on the one account key, so the integration is usage-priced,
    // gated on a per-org opt-in, and metered per operation at the broker
    // chokepoint. `meters` declares WHAT is metered and in WHICH physical unit
    // (per Brad's per-operation decision); the priced rate card
    // (integration_rate_cards) holds the per-unit amount. Each event_type must
    // match what firecrawl-broker writes to integration_usage_events. Until a
    // rate row is seeded an event prices at 0 (budget gate inert).
    premium: {
      pricing: 'usage',
      note: 'Billed per page scraped, page crawled, URL mapped, search query, and monitor check.',
      // NOTE: Firecrawl's /extract endpoint is in maintenance mode (deprecated in
      // @mendable/firecrawl-js), so the extract tool + its meter are intentionally
      // not shipped in v1. Add an `extract` meter here if/when a non-deprecated
      // extraction path is exposed.
      meters: [
        { event_type: 'scrape', unit: 'page' },
        { event_type: 'crawl', unit: 'page' },
        { event_type: 'map', unit: 'url' },
        { event_type: 'search', unit: 'query' },
        { event_type: 'monitor_check', unit: 'check' },
      ],
    },
  },
  {
    // Seed-catalog toolkit (composed by the ultimate-app-coder bundle). Registered
    // here only so the manager's ensureToolkitCli installs the binary via the
    // failure-surfacing cli_tool path (not a customer-installable native -> no
    // `installable`). Single source for the install; the seed integration no
    // longer carries an on_install for it.
    id: 'greenlight',
    name: 'Greenlight (App Store Compliance)',
    category: 'code',
    description:
      'Apple App Store pre-submission compliance scanner by Revyl - scans app source, privacy manifests, binaries, and App Store Connect status for App Review rejection risks',
    supported_auth_types: ['none'],
    capabilities: [
      { id: 'greenlight:scan', name: 'Scan for compliance', description: 'Scan app source, privacy manifests, and binaries for App Store rejection risks', access: 'read' },
    ],
    cli_tool: {
      package: 'greenlight',
      binary: 'greenlight',
      env_key: '',
      // Revyl greenlight is a Go binary. Prefer the Homebrew tap (macOS, and
      // Linuxbrew when present); fall back to `go install` where Go is on the
      // host. Exits non-zero when neither is available so the manager records
      // the failure instead of masking it.
      installer: 'script',
      script:
        'if command -v brew >/dev/null 2>&1; then brew install revylai/tap/greenlight; elif command -v go >/dev/null 2>&1; then go install github.com/RevylAI/greenlight/cmd/greenlight@latest; else echo "greenlight: no supported installer (need brew or go)" >&2; exit 1; fi',
    },
    docs_url: 'https://github.com/RevylAI/greenlight',
  },
  {
    // Seed-catalog toolkit (composed by the ultimate-app-coder bundle). Registered
    // here only for the cli_tool install path; not a customer-installable native.
    id: 'expo',
    name: 'Expo (EAS CLI)',
    category: 'code',
    description:
      'Expo Application Services (EAS) CLI - build, submit, and update iOS and Android apps in the cloud',
    supported_auth_types: ['api_key'],
    capabilities: [
      { id: 'expo:read', name: 'Read builds', description: 'Read EAS build and update status and history', access: 'read' },
      { id: 'expo:ship', name: 'Build and ship', description: 'Trigger cloud builds, submit to the app stores, and publish OTA updates', access: 'write' },
    ],
    cli_tool: {
      package: 'eas-cli',
      binary: 'eas',
      env_key: 'EXPO_TOKEN',
      installer: 'npm',
    },
    docs_url: 'https://docs.expo.dev/eas/',
  },
  {
    id: 'custom',
    name: 'Custom Integration',
    category: 'custom',
    description: 'Connect to any service via API key or webhook',
    supported_auth_types: ['api_key', 'webhook', 'none'],
    capabilities: [
      { id: 'custom:api-access', name: 'API Access', description: 'Generic API access with configured credentials', access: 'read' },
    ],
  },
] as const;

const integrationMap = new Map<string, IntegrationDefinition>(
  INTEGRATION_REGISTRY.map((i) => [i.id, i]),
);

export function getIntegration(id: string): IntegrationDefinition | undefined {
  return integrationMap.get(id);
}

export function getAllIntegrationIds(): IntegrationId[] {
  return INTEGRATION_REGISTRY.map((i) => i.id);
}

/**
 * ENG-7015: a customer-installable NATIVE integration, flattened to the shape
 * the Add Integration picker consumes. This is the single source of truth both
 * the webapp picker (`STATIC_INTEGRATION_OPTIONS`) and the API org allowlist
 * (`NATIVE_PICKER_INTEGRATIONS`) now derive from, so the curated native list is
 * defined exactly once.
 */
export interface InstallableNativeIntegration {
  id: IntegrationId;
  name: string;
  /** Display category label (e.g. "Code"), not the runtime category slug. */
  category: string;
  /** Auth options the connect UI offers (see InstallablePickerMeta.authTypes). */
  authTypes: IntegrationAuthType[];
  beta?: boolean;
}

/**
 * The curated set of customer-installable native integrations, derived from the
 * registry entries that carry an `installable` descriptor. Order follows the
 * registry; both consumers sort by name for display, so it is not significant.
 */
export const INSTALLABLE_NATIVE_INTEGRATIONS: readonly InstallableNativeIntegration[] =
  INTEGRATION_REGISTRY.filter(
    (d): d is IntegrationDefinition & { installable: NonNullable<IntegrationDefinition['installable']> } =>
      d.installable != null,
  ).map((d) => ({
    id: d.id,
    name: d.name,
    category: d.installable.category,
    authTypes: d.installable.authTypes,
    ...(d.beta ? { beta: true } : {}),
  }));

/** Just the ids of {@link INSTALLABLE_NATIVE_INTEGRATIONS} — the API allowlist's input. */
export const INSTALLABLE_NATIVE_INTEGRATION_IDS: readonly IntegrationId[] =
  INSTALLABLE_NATIVE_INTEGRATIONS.map((i) => i.id);
